import React, { useState, useEffect, useMemo } from "react";
import api from "../../api";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import Select from "react-select";
import { FaChalkboardTeacher, FaPlusCircle } from "react-icons/fa";

const ExamSubjectAssignPage = () => {
  const [form, setForm] = useState({
    exam_id: "",
    class_id: "",
    section_id: "",
    // subjects: [
    //   {
    //     subject_id,
    //     subject_name,
    //     selected,
    //     locked,
    //     items: [{ mark_type_id, max_marks, pass_marks, locked, use }]
    //   }
    // ]
    subjects: [],
  });

  const [exams, setExams] = useState([]);
  const [classes, setClasses] = useState([]);
  const [sections, setSections] = useState([]);
  const [subjects, setSubjects] = useState([]);
  const [assigned, setAssigned] = useState([]);
  const [markTypes, setMarkTypes] = useState([]);
  const [headerDefaults, setHeaderDefaults] = useState({});
  const [markTypeUsage, setMarkTypeUsage] = useState({}); // class-wise ON/OFF
  const [loading, setLoading] = useState(false);

  // -----------------------------
  // Initial fetch
  // -----------------------------
  useEffect(() => {
    fetchExams();
    fetchClasses();
    fetchMarkTypes();
  }, []);

  // markTypes আসার পর default usage = true
  useEffect(() => {
    if (!markTypes.length) {
      setMarkTypeUsage({});
      return;
    }
    setMarkTypeUsage((prev) => {
      const next = { ...prev };
      markTypes.forEach((mt) => {
        if (typeof next[mt.id] === "undefined") {
          next[mt.id] = true; // by default সব mark type ON
        }
      });
      return next;
    });
  }, [markTypes]);

  // subjects + markTypes + assigned পরিবর্তন হলে table rows বানাই
  useEffect(() => {
    if (!subjects.length || !markTypes.length) return;

    const currentClassName =
      classes.find((c) => String(c.id) === String(form.class_id))
        ?.class_name || null;

    const currentSectionName =
      sections.find((s) => String(s.id) === String(form.section_id))
        ?.section_name || null;

    const rows = subjects.map((sub) => {
      const items = markTypes.map((mt) => {
        const match = assigned.find(
          (a) =>
            String(a.subject_id) === String(sub.subject_id) &&
            a.title === mt.title &&
            (!currentClassName || a.class_name === currentClassName) &&
            (!currentSectionName || a.section_name === currentSectionName)
        );

        const defaultUse =
          typeof markTypeUsage[mt.id] !== "undefined"
            ? markTypeUsage[mt.id]
            : true;

        return {
          mark_type_id: mt.id,
          max_marks: match ? match.max_marks : "",
          pass_marks: match ? match.pass_marks : "",
          locked: !!match,
          // already assigned হলে সবসময় use = true
          use: match ? true : defaultUse,
        };
      });

      const rowLocked = items.some((i) => i.locked);

      return {
        subject_id: sub.subject_id,
        subject_name: sub.subject_name,
        selected: true,
        locked: rowLocked,
        items,
      };
    });

    setForm((prev) => ({
      ...prev,
      subjects: rows,
    }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    subjects,
    markTypes,
    assigned,
    form.class_id,
    form.section_id,
    classes,
    sections,
  ]);

  // -----------------------------
  // Toast helper
  // -----------------------------
  const showToast = (type, message) => {
    const options = {
      position: "top-right",
      autoClose: 2000,
      hideProgressBar: false,
      closeOnClick: true,
      pauseOnHover: true,
      draggable: true,
      theme: "colored",
    };
    switch (type) {
      case "success":
        toast.success(message, options);
        break;
      case "error":
        toast.error(message, options);
        break;
      case "warning":
        toast.warning(message, options);
        break;
      default:
        toast.info(message, options);
    }
  };

  // -----------------------------
  // API calls
  // -----------------------------
  const fetchExams = async () => {
    try {
      const res = await api.get("/exam-management/list");
      if (res.data?.status && res.data.data.length) {
        setExams(res.data.data);
      } else {
        setExams([]);
        showToast("warning", "No exams found!");
      }
    } catch {
      showToast("error", "Failed to load exams");
    }
  };

  const fetchClasses = async () => {
    try {
      const res = await api.get("/get-classes");
      if (res.data?.success && res.data.data.length) {
        setClasses(res.data.data);
      } else {
        setClasses([]);
        showToast("warning", "No classes found!");
      }
    } catch {
      showToast("error", "Failed to load classes");
    }
  };

  const fetchSections = async (classId) => {
    try {
      const res = await api.get(`/get-sections/${classId}`);
      if (res.data?.success && res.data.data.length) {
        setSections(res.data.data);
      } else {
        setSections([]);
        showToast("warning", "No sections found!");
      }
    } catch {
      setSections([]);
      showToast("error", "Failed to load sections");
    }
  };

  const fetchSubjects = async (classId, sectionId) => {
    if (!classId || !sectionId) {
      setSubjects([]);
      return;
    }
    try {
      const res = await api.get(
        `/class-subjects/list?class_id=${classId}&section_id=${sectionId}`
      );
      if (res.data?.status && res.data.data.length) {
        setSubjects(res.data.data);
      } else {
        setSubjects([]);
        showToast("warning", "No subjects found for this class & section!");
      }
    } catch {
      setSubjects([]);
      showToast("error", "Failed to load subjects");
    }
  };

  const fetchMarkTypes = async () => {
    try {
      const res = await api.get("/mark-types/list");
      if (res.data?.status && res.data.data?.length) {
        setMarkTypes(res.data.data);
      } else {
        setMarkTypes([]);
        showToast("warning", "No marks types found! Please create first.");
      }
    } catch {
      setMarkTypes([]);
      showToast("error", "Failed to load marks types");
    }
  };

  const fetchAssigned = async (examId) => {
    if (!examId) return;
    try {
      const res = await api.get(`/exam-subjects/list?exam_id=${examId}`);
      if (res.data?.status && res.data.data.length) {
        setAssigned(res.data.data);
      } else {
        setAssigned([]);
      }
    } catch {
      setAssigned([]);
      showToast("error", "Failed to fetch assigned subjects!");
    }
  };

  // -----------------------------
  // Handlers
  // -----------------------------
  const handleExamChange = (option) => {
    const id = option ? option.value : "";
    setForm((prev) => ({
      ...prev,
      exam_id: id,
      class_id: "",
      section_id: "",
      subjects: [],
    }));
    setSections([]);
    setSubjects([]);
    setHeaderDefaults({});

    if (id) fetchAssigned(id);
    else {
      setAssigned([]);
      showToast("info", "Exam deselected, cleared assigned list!");
    }
  };

  const handleClassChange = (option) => {
    const id = option ? option.value : "";
    setForm((prev) => ({
      ...prev,
      class_id: id,
      section_id: "",
      subjects: [],
    }));
    setSections([]);
    setSubjects([]);
    setHeaderDefaults({});
    if (id) fetchSections(id);
  };

  const handleSectionChange = (option) => {
    const id = option ? option.value : "";
    setForm((prev) => ({
      ...prev,
      section_id: id,
      subjects: [],
    }));
    setSubjects([]);
    setHeaderDefaults({});
    if (form.class_id && id) fetchSubjects(form.class_id, id);
  };

  const handleSubjectSelectToggle = (subject_id) => {
    setForm((prev) => ({
      ...prev,
      subjects: prev.subjects.map((sub) =>
        sub.subject_id === subject_id
          ? { ...sub, selected: !sub.selected }
          : sub
      ),
    }));
  };


  // class-wise mark type toggle (master switch)
  const toggleMarkTypeUsage = (mark_type_id) => {
    const newVal = !markTypeUsage[mark_type_id];

    setMarkTypeUsage((prev) => ({
      ...prev,
      [mark_type_id]: newVal,
    }));

    setForm((prev) => ({
      ...prev,
      subjects: prev.subjects.map((sub) => ({
        ...sub,
        items: sub.items.map((it) =>
          it.mark_type_id === mark_type_id
            ? { ...it, use: newVal }
            : it
        ),
      })),
    }));
  };


  // column header auto-fill (selected mark types only)
  const handleHeaderChange = (mark_type_id, field, value) => {
    setHeaderDefaults((prev) => ({
      ...prev,
      [mark_type_id]: {
        ...(prev[mark_type_id] || {}),
        [field]: value,
      },
    }));

    setForm((prev) => ({
      ...prev,
      subjects: prev.subjects.map((sub) => ({
        ...sub,
        items: sub.items.map((it) =>
          it.mark_type_id === mark_type_id
            ? { ...it, [field]: value }
            : it
        ),
      })),
    }));
  };


  const handleCellChange = (subject_id, mark_type_id, field, value) => {
    setForm((prev) => ({
      ...prev,
      subjects: prev.subjects.map((sub) => {
        if (sub.subject_id !== subject_id) return sub;

        return {
          ...sub,
          items: sub.items.map((it) =>
            it.mark_type_id === mark_type_id
              ? { ...it, [field]: value }
              : it
          ),
        };
      }),
    }));
  };


  // subject-wise mark type ON/OFF
  const handleItemUseToggle = (subject_id, mark_type_id) => {
    setForm((prev) => ({
      ...prev,
      subjects: prev.subjects.map((sub) => {
        if (sub.subject_id !== subject_id) return sub;

        return {
          ...sub,
          items: sub.items.map((it) =>
            it.mark_type_id === mark_type_id
              ? { ...it, use: !it.use }
              : it
          ),
        };
      }),
    }));
  };


  const handleAddSubject = async () => {
    if (loading) return;

    if (!form.exam_id)
      return showToast("warning", "Please select an exam first!");
    if (!form.class_id)
      return showToast("warning", "Please select a class first!");
    if (!form.section_id)
      return showToast("warning", "Please select a section first!");
    if (!form.subjects.length)
      return showToast("warning", "No subjects found to assign!");

    const activeMarkTypes = markTypes.filter((mt) => markTypeUsage[mt.id]);
    if (!activeMarkTypes.length) {
      return showToast("warning", "Please select at least one mark type!");
    }

    const selectedSubjects = form.subjects.filter(
      (s) => s.selected && !s.locked
    );
    if (!selectedSubjects.length) {
      return showToast(
        "warning",
        "No new subjects/marks to assign for this exam!"
      );
    }

    const formattedSubjects = selectedSubjects.map((sub) => {
      const itemsPayload = sub.items
        // class-wise ON + subject-wise use = true
        .filter((i) => markTypeUsage[i.mark_type_id] && i.use)
        .map((i) => {
          const mt = markTypes.find((mt) => mt.id === i.mark_type_id);
          const hasPass = mt?.has_pass === "1";

          return {
            mark_type_id: i.mark_type_id,
            max_marks: i.max_marks ? parseFloat(i.max_marks) : 0,
            pass_marks: hasPass
              ? i.pass_marks
                ? parseFloat(i.pass_marks)
                : 0
              : 0,
          };
        })
        .filter((i) => i.max_marks > 0); // যেখানে max = 0, সেটা পাঠানোর দরকার নেই

      return {
        subject_id: sub.subject_id,
        items: itemsPayload,
      };
    });

    setLoading(true);

    try {
      const res = await api.post("/exam-subjects/add", {
        exam_id: form.exam_id,
        class_id: form.class_id,
        section_id: form.section_id,
        subjects: formattedSubjects,
      });

      if (res.data?.status) {
        showToast(
          "success",
          res.data?.message || "Subjects assigned successfully!"
        );

        setForm((prev) => ({
          exam_id: prev.exam_id,
          class_id: "",
          section_id: "",
          subjects: [],
        }));
        setSections([]);
        setSubjects([]);
        setHeaderDefaults({});
        fetchAssigned(form.exam_id);
      } else {
        showToast("error", res.data?.message || "Assignment failed!");
      }
    } catch (err) {
      const backendMsg =
        err?.response?.data?.message ||
        err?.message ||
        "Unexpected server error!";
      showToast("error", backendMsg);
    } finally {
      setLoading(false);
    }
  };

  const handleSaveSubjects = async () => {
    if (loading) return;

    if (!form.exam_id)
      return showToast("warning", "Please select an exam first!");
    if (!form.class_id)
      return showToast("warning", "Please select a class first!");
    if (!form.section_id)
      return showToast("warning", "Please select a section first!");

    const activeMarkTypes = markTypes.filter(
      (mt) => markTypeUsage[mt.id]
    );
    if (!activeMarkTypes.length) {
      return showToast("warning", "Please select at least one mark type!");
    }

    const selectedSubjects = form.subjects.filter((s) => s.selected);
    if (!selectedSubjects.length) {
      return showToast("warning", "Please select at least one subject!");
    }

    const formattedSubjects = selectedSubjects.map((sub) => {
      const itemsPayload = sub.items
        .filter((i) => markTypeUsage[i.mark_type_id] && i.use)
        .map((i) => {
          const mt = markTypes.find((m) => m.id === i.mark_type_id);
          const hasPass = mt?.has_pass === "1";

          return {
            mark_type_id: i.mark_type_id,
            max_marks: Number(i.max_marks || 0),
            pass_marks: hasPass ? Number(i.pass_marks || 0) : 0,
          };
        })
        .filter((i) => i.max_marks > 0);

      const isAssigned = assigned.some(
        (a) => String(a.subject_id) === String(sub.subject_id)
      );


      return {
        subject_id: sub.subject_id,
        subject_name: sub.subject_name,
        selected: true,
        items: itemsPayload,
        assigned: isAssigned,   // 🔥 THIS LINE
        locked: false,          // optional, since readonly OFF
      };

    });

    const addSubjects = formattedSubjects.filter((s) => !s.assigned);
    const updateSubjects = formattedSubjects.filter((s) => s.assigned);

    setLoading(true);

    try {
      // 🔹 ADD
      if (addSubjects.length) {
        await api.post("/exam-subjects/add", {
          exam_id: form.exam_id,
          class_id: form.class_id,
          section_id: form.section_id,
          subjects: addSubjects,
        });
      }

      // 🔹 UPDATE
      if (updateSubjects.length) {
        await api.post("/exam-subjects/update", {
          exam_id: form.exam_id,
          class_id: form.class_id,
          section_id: form.section_id,
          subjects: updateSubjects,
        });
      }

      showToast("success", "Subjects saved successfully!");

      fetchAssigned(form.exam_id);

    } catch (err) {
      showToast(
        "error",
        err?.response?.data?.message || "Server error!"
      );
    } finally {
      setLoading(false);
    }
  };

  // -----------------------------
  // Assigned data grouping
  // -----------------------------
  const groupedAssigned = useMemo(() => {
    const map = {};
    assigned.forEach((row) => {
      const key = `${row.class_name}-${row.section_name}-${row.subject_id}`;
      if (!map[key]) {
        map[key] = {
          class_name: row.class_name,
          section_name: row.section_name,
          subject_name: row.subject_name,
          marks: [],
        };
      }
      map[key].marks.push(row);
    });
    return Object.values(map);
  }, [assigned]);

  const activeMarkTypes = markTypes.filter((mt) => markTypeUsage[mt.id]);

  const hasAssignedSelected = form.subjects.some(
    (s) => s.selected && s.assigned
  );

  const hasNewSelected = form.subjects.some(
    (s) => s.selected && !s.assigned
  );

  const buttonText = loading
    ? "Saving..."
    : hasAssignedSelected && hasNewSelected
      ? "Save Changes"
      : hasAssignedSelected
        ? "Update Subjects"
        : "Assign Subjects";

  // -----------------------------
  // Styles & JSX
  // -----------------------------
  return (
    <>
      <ToastContainer />

      <style>{`
        .assign-container {
          min-height: 100vh;
          background: #f6f9ff;
          font-family: 'Poppins', sans-serif;
        }
        .assign-header {
          background: linear-gradient(135deg, #3b82f6, #2563eb, #1e40af);
          color: white;
          padding: 60px 20px 100px;
          text-align: center;
          border-bottom-left-radius: 50% 10%;
          border-bottom-right-radius: 50% 10%;
          box-shadow: 0 8px 25px rgba(59, 130, 246, 0.4);
        }
        .assign-header h1 {
          font-size: 32px;
          font-weight: 800;
          display: flex;
          justify-content: center;
          align-items: center;
          gap: 10px;
        }
        .assign-card {
          max-width: 95%;
          margin: -60px auto 0;
          background: white;
          border-radius: 20px;
          padding: 30px;
          box-shadow: 0 10px 30px rgba(0, 0, 0, 0.08);
        }
        .assign-form {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
          gap: 16px;
          margin-bottom: 16px;
        }

        .marktype-toggle-bar {
          margin-bottom: 12px;
          padding: 10px 14px;
          border-radius: 12px;
          background: #eff6ff;
          display: flex;
          align-items: center;
          flex-wrap: wrap;
          gap: 10px;
          justify-content: space-between;
        }

        .marktype-toggle-title {
          font-size: 13px;
          font-weight: 600;
          color: #1e3a8a;
        }

        .marktype-toggle-group {
          display: flex;
          flex-wrap: wrap;
          gap: 10px;
        }

        .marktype-toggle-chip {
          display: inline-flex;
          align-items: center;
          gap: 6px;
          padding: 4px 10px;
          border-radius: 999px;
          border: 1px solid #bfdbfe;
          background: #dbeafe;
          font-size: 12px;
          cursor: pointer;
          user-select: none;
        }

        .marktype-toggle-chip.inactive {
          background: #e5e7eb;
          border-color: #d1d5db;
          color: #6b7280;
        }

        .marktype-toggle-chip input {
          width: 14px;
          height: 14px;
          accent-color: #2563eb;
        }

        .marks-table-wrapper {
          margin-top: 10px;
          overflow-x: auto;
        }

        .marks-table {
          width: 100%;
          border-collapse: collapse;
          min-width: 600px;
        }

        .marks-table thead tr:first-child {
          background: linear-gradient(90deg, #2563eb, #3b82f6);
          color: white;
        }

        .marks-table thead tr:nth-child(2) {
          background: #e5efff;
        }

        .marks-table th,
        .marks-table td {
          border: 1px solid #e5e7eb;
          padding: 8px 10px;
          font-size: 13px;
          text-align: center;
        }

        .marks-table th.subject-col,
        .marks-table td.subject-col {
          text-align: left;
          padding-left: 14px;
          font-weight: 600;
        }

        .marks-table tr.locked-row td.subject-col {
          color: #6b7280;
        }

        .marks-table th.select-col,
        .marks-table td.select-col {
          width: 60px;
        }

        .marks-table input[type="number"] {
          width: 80px;
          padding: 5px 6px;
          border-radius: 6px;
          border: 1px solid #d1d5db;
          font-size: 12px;
          text-align: center;
        }

        .marks-table input[type="number"].readonly-input {
          background: #e5e7eb;
          cursor: not-allowed;
          border-color: #d1d5db;
        }

        .marks-table input[type="checkbox"] {
          width: 16px;
          height: 16px;
          accent-color: #2563eb;
        }

        .marks-header-title {
          font-size: 13px;
          font-weight: 600;
        }

        .marks-header-subtitle {
          font-size: 11px;
          opacity: 0.9;
        }

        .header-input {
          width: 70px;
          padding: 4px 5px;
          border-radius: 6px;
          border: 1px solid #cbd5f5;
          font-size: 11px;
          text-align: center;
          background: white;
        }

        .cell-mark-wrap {
          display: flex;
          flex-direction: column;
          align-items: center;
          gap: 4px;
        }

        .use-toggle {
          display: flex;
          align-items: center;
          gap: 4px;
          font-size: 11px;
          color: #4b5563;
        }

        .use-toggle input {
          width: 14px;
          height: 14px;
        }

        .assign-btn {
          background: linear-gradient(90deg, #2563eb, #3b82f6);
          color: white;
          font-weight: 600;
          border: none;
          border-radius: 10px;
          cursor: pointer;
          transition: 0.3s;
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 8px;
          padding: 12px 20px;
          margin-top: 22px;
        }
        .assign-btn:hover {
          transform: translateY(-2px);
          box-shadow: 0 8px 20px rgba(37, 99, 235, 0.6);
        }

        .table-card {
          max-width: 95%;
          margin: 40px auto;
          background: white;
          border-radius: 16px;
          box-shadow: 0 10px 25px rgba(0, 0, 0, 0.07);
          overflow: hidden;
        }
        .assigned-table {
          width: 100%;
          border-collapse: collapse;
        }
        .assigned-table thead {
          background: linear-gradient(90deg, #2563eb, #3b82f6);
          color: white;
        }
        .assigned-table th,
        .assigned-table td {
          padding: 12px 14px;
          font-size: 14px;
          text-align: left;
        }
        .assigned-table tr:nth-child(even) {
          background: #f9fafb;
        }

        @keyframes spin {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }
      `}</style>

      <div className="assign-container">
        <div className="assign-header">
          <h1>
            <FaChalkboardTeacher /> Exam Subject Assign
          </h1>
          <p>Assign subjects with dynamic mark types to each exam</p>
        </div>

        {/* Form + Table */}
        <div className="assign-card">
          <div className="assign-form">
            {/* Exam */}
            <Select
              options={exams.map((exam) => ({
                label: exam.exam_name,
                value: exam.id,
              }))}
              placeholder="Select Exam"
              value={
                exams
                  .map((e) => ({ label: e.exam_name, value: e.id }))
                  .find((x) => x.value === form.exam_id) || null
              }
              onChange={handleExamChange}
              isClearable
              isSearchable
            />

            {/* Class */}
            <Select
              options={classes.map((cls) => ({
                label: cls.class_name,
                value: cls.id,
              }))}
              placeholder="Select Class"
              value={
                classes
                  .map((c) => ({ label: c.class_name, value: c.id }))
                  .find((x) => x.value === form.class_id) || null
              }
              onChange={handleClassChange}
              isClearable
              isSearchable
            />

            {/* Section */}
            <Select
              options={sections.map((s) => ({
                label: s.section_name,
                value: s.id,
              }))}
              placeholder="Select Section"
              value={
                sections
                  .map((s) => ({ label: s.section_name, value: s.id }))
                  .find((x) => x.value === form.section_id) || null
              }
              onChange={handleSectionChange}
              isClearable
              isDisabled={!form.class_id}
            />
          </div>

          {/* Class-wise Mark Type selection */}
          {markTypes.length > 0 && (
            <div className="marktype-toggle-bar">
              <div className="marktype-toggle-title">
                Mark Types for this class:
              </div>
              <div className="marktype-toggle-group">
                {markTypes.map((mt) => (
                  <label
                    key={mt.id}
                    className={
                      "marktype-toggle-chip " +
                      (markTypeUsage[mt.id] ? "" : "inactive")
                    }
                  >
                    <input
                      type="checkbox"
                      checked={!!markTypeUsage[mt.id]}
                      onChange={() => toggleMarkTypeUsage(mt.id)}
                    />
                    <span>{mt.title}</span>
                  </label>
                ))}
              </div>
            </div>
          )}

          {/* Marks Table */}
          <div className="marks-table-wrapper">
            {form.subjects.length > 0 && activeMarkTypes.length > 0 ? (
              <table className="marks-table">
                <thead>
                  {/* First header row: titles */}
                  <tr>
                    <th className="select-col">Select</th>
                    <th className="subject-col">Subject</th>

                    {activeMarkTypes.map((mt) => (
                      <React.Fragment key={mt.id}>
                        <th colSpan={mt.has_pass === "1" ? 2 : 1}>
                          <div className="marks-header-title">{mt.title}</div>
                          <div className="marks-header-subtitle">
                            {mt.has_pass === "1"
                              ? "Max & Pass Marks"
                              : "Max Marks"}
                          </div>
                        </th>
                      </React.Fragment>
                    ))}
                  </tr>

                  {/* Second header row: Max(All) / Pass(All) inputs */}
                  <tr>
                    <th></th>
                    <th></th>

                    {activeMarkTypes.map((mt) => (
                      <React.Fragment key={mt.id}>
                        <th>
                          <input
                            type="number"
                            className="header-input"
                            value={headerDefaults[mt.id]?.max_marks || ""}
                            onChange={(e) =>
                              handleHeaderChange(
                                mt.id,
                                "max_marks",
                                e.target.value
                              )
                            }
                            placeholder="Max"
                          />
                        </th>

                        {mt.has_pass === "1" && (
                          <th>
                            <input
                              type="number"
                              className="header-input"
                              value={headerDefaults[mt.id]?.pass_marks || ""}
                              onChange={(e) =>
                                handleHeaderChange(
                                  mt.id,
                                  "pass_marks",
                                  e.target.value
                                )
                              }
                              placeholder="Pass"
                            />
                          </th>
                        )}
                      </React.Fragment>
                    ))}
                  </tr>
                </thead>

                <tbody>
                  {form.subjects.map((sub) => (
                    <tr
                      key={sub.subject_id}
                    //className={sub.locked ? "locked-row" : ""}
                    >
                      <td className="select-col">
                        <input
                          type="checkbox"
                          checked={sub.selected}
                          onChange={() =>
                            handleSubjectSelectToggle(sub.subject_id)
                          }
                        //disabled={sub.locked}
                        />
                      </td>

                      <td className="subject-col">{sub.subject_name}</td>

                      {activeMarkTypes.map((mt) => {
                        const item =
                          sub.items.find((i) => i.mark_type_id === mt.id) || {};
                        //const isUsed = item.locked || item.use;
                        const isUsed = item.use;
                        const maxReadOnlyClass = item.locked
                          ? "readonly-input"
                          : "";

                        return (
                          <React.Fragment key={mt.id}>
                            {/* Max + Use toggle */}
                            <td>
                              <div className="cell-mark-wrap">
                                <label className="use-toggle">
                                  <input
                                    type="checkbox"
                                    checked={isUsed}
                                    //disabled={item.locked}
                                    onChange={() =>
                                      handleItemUseToggle(
                                        sub.subject_id,
                                        mt.id
                                      )
                                    }
                                  />
                                  <span>Use</span>
                                </label>

                                {isUsed && (
                                  <input
                                    type="number"
                                    //className={maxReadOnlyClass}
                                    value={item.max_marks || ""}
                                    onChange={(e) =>
                                      handleCellChange(
                                        sub.subject_id,
                                        mt.id,
                                        "max_marks",
                                        e.target.value
                                      )
                                    }
                                    placeholder="Max"
                                  //readOnly={item.locked}
                                  />
                                )}
                              </div>
                            </td>

                            {/* Pass input only if has_pass === "1" */}
                            {mt.has_pass === "1" && (
                              <td>
                                {isUsed && (
                                  <input
                                    type="number"
                                    // className={
                                    //   item.locked ? "readonly-input" : ""
                                    // }
                                    value={item.pass_marks || ""}
                                    onChange={(e) =>
                                      handleCellChange(
                                        sub.subject_id,
                                        mt.id,
                                        "pass_marks",
                                        e.target.value
                                      )
                                    }
                                    placeholder="Pass"
                                  //readOnly={item.locked}
                                  />
                                )}
                              </td>
                            )}
                          </React.Fragment>
                        );
                      })}
                    </tr>
                  ))}
                </tbody>
              </table>
            ) : (
              <p
                style={{
                  textAlign: "center",
                  color: "#6b7280",
                  fontSize: 13,
                }}
              >
                {form.subjects.length === 0
                  ? "Select Exam, Class & Section to load subjects."
                  : "Please turn ON at least one mark type for this class."}
              </p>
            )}
          </div>

          <button
            onClick={handleSaveSubjects}
            className="assign-btn"
            disabled={loading || !markTypes.length}
            style={{
              opacity: loading || !markTypes.length ? 0.7 : 1,
              cursor:
                loading || !markTypes.length ? "not-allowed" : "pointer",
            }}
          >
            {loading ? (
              <>
                <span
                  className="spinner"
                  style={{
                    width: "18px",
                    height: "18px",
                    border: "3px solid #fff",
                    borderTop: "3px solid transparent",
                    borderRadius: "50%",
                    display: "inline-block",
                    marginRight: "8px",
                    animation: "spin 0.8s linear infinite",
                  }}
                ></span>
                Saving...
              </>
            ) : (
              <>
                <FaPlusCircle /> {buttonText}
              </>
            )}
          </button>

        </div>

        {/* Assigned list table */}
        <div className="table-card">
          <table className="assigned-table">
            <thead>
              <tr>
                <th>Class</th>
                <th>Section</th>
                <th>Subject</th>
                <th>Marks Structure</th>
              </tr>
            </thead>
            <tbody>
              {groupedAssigned.length > 0 ? (
                groupedAssigned.map((row, i) => (
                  <tr key={i}>
                    <td>{row.class_name}</td>
                    <td>{row.section_name}</td>
                    <td>{row.subject_name}</td>
                    <td>
                      {row.marks
                        .map((m) => {
                          const max = parseFloat(m.max_marks);
                          const pass = parseFloat(m.pass_marks);
                          const mt = markTypes.find(
                            (mt) => mt.title === m.title
                          );
                          const hasPass = mt?.has_pass === "1";

                          if (hasPass && pass > 0) {
                            return `${m.title}: ${max}/${pass}`;
                          }
                          return `${m.title}: ${max}`;
                        })
                        .join(", ")}
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td
                    colSpan={4}
                    style={{
                      textAlign: "center",
                      padding: "15px",
                      color: "#999",
                    }}
                  >
                    No subjects assigned yet
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </>
  );
};

export default ExamSubjectAssignPage;
